import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FiArrowLeft,
  FiCalendar,
  FiClock,
  FiLayers,
  FiSave,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi'
import {
  fetchAssignedBatches as fetchAssignedBatchesApi,
  fetchBatchDetail as fetchBatchDetailApi,
  createLecture as createLectureApi,
} from '../../api/staffTeachingApi'
import './CreateLecture.css'

const datetimeLocalValue = (value) => {
  if (!value) return ''
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return ''
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

const CreateLecture = () => {
  const navigate = useNavigate()
  const defaultWindowRef = useRef(null)
  if (!defaultWindowRef.current) {
    const start = new Date(Date.now() + 15 * 60 * 1000)
    const end = new Date(start.getTime() + 60 * 60 * 1000)
    defaultWindowRef.current = { start, end }
  }

  const [batches, setBatches] = useState([])
  const [batchesLoading, setBatchesLoading] = useState(true)
  const [batchesError, setBatchesError] = useState(null)

  const [selectedBatchId, setSelectedBatchId] = useState('')
  const [modules, setModules] = useState([])
  const [modulesLoading, setModulesLoading] = useState(false)
  const [modulesError, setModulesError] = useState(null)

  const [topic, setTopic] = useState('')
  const [description, setDescription] = useState('')
  const [moduleId, setModuleId] = useState('')
  const [startAt, setStartAt] = useState(() => datetimeLocalValue(defaultWindowRef.current.start))
  const [endAt, setEndAt] = useState(() => datetimeLocalValue(defaultWindowRef.current.end))
  const [meetingLink, setMeetingLink] = useState('')
  const [createFermion, setCreateFermion] = useState(true)

  const [submissionError, setSubmissionError] = useState(null)
  const [submissionSuccess, setSubmissionSuccess] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadAssignedBatches = useCallback(async () => {
    setBatchesLoading(true)
    setBatchesError(null)
    try {
      const data = await fetchAssignedBatchesApi()
      setBatches(Array.isArray(data) ? data : [])
      if (Array.isArray(data) && data.length) {
        setSelectedBatchId((prev) => prev || data[0]._id)
      }
    } catch (apiError) {
      console.error('Failed to load batches for lecture creation', apiError)
      setBatchesError(apiError?.response?.data?.message || apiError.message || 'Unable to load batches')
    } finally {
      setBatchesLoading(false)
    }
  }, [])

  const loadModulesForBatch = useCallback(
    async (batchId) => {
      if (!batchId) {
        setModules([])
        setModuleId('')
        return
      }

      setModulesLoading(true)
      setModulesError(null)
      try {
        const detail = await fetchBatchDetailApi(batchId)
        const moduleList = detail?.modules || []
        setModules(moduleList)
        if (moduleList.length) {
          setModuleId((previous) => {
            if (previous === '') {
              return previous
            }
            if (previous && moduleList.some((module) => module._id === previous)) {
              return previous
            }
            return moduleList[0]._id
          })
        } else {
          setModuleId('')
        }
      } catch (apiError) {
        console.error('Failed to load modules for batch', apiError)
        setModulesError(apiError?.response?.data?.message || apiError.message || 'Unable to load modules')
        setModules([])
        setModuleId('')
      } finally {
        setModulesLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    loadAssignedBatches()
  }, [loadAssignedBatches])

  useEffect(() => {
    if (selectedBatchId) {
      loadModulesForBatch(selectedBatchId)
    } else {
      setModules([])
      setModuleId('')
    }
  }, [selectedBatchId, loadModulesForBatch])

  const selectedBatch = useMemo(() => batches.find((batch) => batch._id === selectedBatchId), [batches, selectedBatchId])
  const startDate = useMemo(() => (startAt ? new Date(startAt) : null), [startAt])
  const endDate = useMemo(() => (endAt ? new Date(endAt) : null), [endAt])
  const scheduleInvalid = useMemo(() => {
    if (!startDate || !endDate) return false
    return Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate >= endDate
  }, [startDate, endDate])

  const disableSubmit =
    isSubmitting || !selectedBatchId || !topic.trim() || !startAt || !endAt || scheduleInvalid

  const handleBatchChange = (event) => {
    const nextBatchId = event.target.value
    setSelectedBatchId(nextBatchId)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (disableSubmit) return

    setSubmissionError(null)
    setSubmissionSuccess(null)
    setIsSubmitting(true)

    if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate >= endDate) {
      setSubmissionError('Provide a valid start and end time for the lecture.')
      setIsSubmitting(false)
      return
    }

    const payload = {
      batchId: selectedBatchId,
      moduleId: moduleId || undefined,
      topic: topic.trim(),
      description: description.trim() || undefined,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
      meetingLink: meetingLink.trim() || undefined,
      createFermion,
    }

    try {
      const response = await createLectureApi(payload)
      setSubmissionSuccess('Lecture scheduled successfully.')
      if (response?.data?._id || response?._id) {
        const lectureId = response?.data?._id || response?._id
        setTimeout(() => {
          navigate(`/lectures/${lectureId}`)
        }, 450)
      } else if (selectedBatchId) {
        setTimeout(() => {
          navigate(`/batches/${selectedBatchId}`)
        }, 450)
      }
    } catch (apiError) {
      console.error('Failed to create lecture', apiError)
      setSubmissionError(apiError?.response?.data?.message || apiError.message || 'Unable to create lecture')
    } finally {
      setIsSubmitting(false)
    }
  }

  const pageState = useMemo(() => {
    if (batchesLoading) return 'loading'
    if (batchesError) return 'error'
    if (!batches.length) return 'empty'
    return 'ready'
  }, [batchesLoading, batchesError, batches.length])

  return (
    <div className="create-lecture">
      <header className="create-lecture__header">
        <button type="button" className="back-link" onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} /> Back
        </button>
        <h1>Create New Lecture</h1>
        <p>Schedule a new lecture for your batch</p>
      </header>

      {pageState === 'loading' && <div className="create-lecture__state">Loading batches…</div>}

      {pageState === 'error' && (
        <div className="create-lecture__alert is-error">
          <FiAlertCircle size={18} /> {batchesError}
        </div>
      )}

      {pageState === 'empty' && (
        <div className="create-lecture__state">No batches found. You need at least one assigned batch to schedule lectures.</div>
      )}

      {pageState === 'ready' && (
        <form className="create-lecture__form" onSubmit={handleSubmit}>
          <section className="form-section">
            <h2>Lecture context</h2>
            <div className="form-grid">
              <label className="form-control">
                <span>Batch</span>
                <div className="select-wrapper">
                  <FiLayers size={16} />
                  <select value={selectedBatchId} onChange={handleBatchChange} required>
                    <option value="" disabled>
                      Select batch
                    </option>
                    {batches.map((batch) => (
                      <option key={batch._id} value={batch._id}>
                        {batch.batchName}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedBatch && (
                  <small>
                    {selectedBatch.course?.title || 'Programme'} · {selectedBatch.studentCount || 0} students
                  </small>
                )}
              </label>

              <label className="form-control">
                <span>Module (optional)</span>
                <div className={`select-wrapper ${modulesLoading ? 'is-loading' : ''}`}>
                  <FiLayers size={16} />
                  <select value={moduleId} onChange={(event) => setModuleId(event.target.value)} disabled={modulesLoading}>
                    {modulesLoading ? (
                      <option value="">Loading modules…</option>
                    ) : (
                      <>
                        <option value="">No module (batch level)</option>
                        {modules.length ? (
                          modules.map((module) => (
                            <option key={module._id} value={module._id}>
                              Module {module.order || module.sequence || 1}: {module.title}
                            </option>
                          ))
                        ) : (
                          <option value="__no-modules" disabled>
                            No modules available
                          </option>
                        )}
                      </>
                    )}
                  </select>
                </div>
                {modulesError && <small className="error-text">{modulesError}</small>}
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2>Session details</h2>
            <div className="form-grid">
              <label className="form-control">
                <span>Topic</span>
                <input
                  type="text"
                  placeholder="Introduction to advanced recursion"
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                  required
                />
              </label>

              <label className="form-control">
                <span>Description</span>
                <textarea
                  placeholder="Outline the key talking points or share pre-work for learners."
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={4}
                />
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2>Scheduling</h2>
            <div className="form-grid">
              <label className="form-control">
                <span>Start time</span>
                <div className="input-with-icon">
                  <FiCalendar size={16} />
                  <input type="datetime-local" value={startAt} onChange={(event) => setStartAt(event.target.value)} required />
                </div>
              </label>

              <label className="form-control">
                <span>End time</span>
                <div className="input-with-icon">
                  <FiClock size={16} />
                  <input type="datetime-local" value={endAt} onChange={(event) => setEndAt(event.target.value)} required />
                </div>
                {scheduleInvalid && (
                  <small className="error-text">End time must be after the start time.</small>
                )}
              </label>

              <label className="form-control">
                <span>Meeting link (optional)</span>
                <input
                  type="url"
                  placeholder="https://meet.google.com/..."
                  value={meetingLink}
                  onChange={(event) => setMeetingLink(event.target.value)}
                />
                <small>Paste your Google Meet link here, or leave blank to auto-generate.</small>
              </label>

              <label className="toggle-control">
                <input type="checkbox" checked={createFermion} onChange={(event) => setCreateFermion(event.target.checked)} />
                <span>Auto-create Google Meet link</span>
              </label>
            </div>
          </section>

          {submissionError && (
            <div className="create-lecture__alert is-error">
              <FiAlertCircle size={18} /> {submissionError}
            </div>
          )}

          {submissionSuccess && (
            <div className="create-lecture__alert is-success">
              <FiCheckCircle size={18} /> {submissionSuccess}
            </div>
          )}

          <footer className="form-actions">
            <button type="button" className="muted-button" onClick={() => navigate('/lectures')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="primary-button" disabled={disableSubmit}>
              <FiSave size={18} />
              {isSubmitting ? 'Scheduling…' : 'Schedule lecture'}
            </button>
          </footer>
        </form>
      )}
    </div>
  )
}

export default CreateLecture
